import React, { useState, useEffect, useRef } from 'react';
import { supabase } from "../../supabaseClient";
import { getSupaIdToLocalIdMap } from "../../utils/teamIdMap";
import "./TeamsPage.css";

// Stats for a finished season never change, so cache each season's fetched/
// translated result at module scope - switching back to it is then instant.
const statsCache = new Map();

const TeamsPage = ({ teams }) => {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [isSeasonDropdownOpen, setIsSeasonDropdownOpen] = useState(false);
  const [teamStats, setTeamStats] = useState({});
  const [homeStats, setHomeStats] = useState({});
  const [awayStats, setAwayStats] = useState({});
  const [loading, setLoading] = useState(true);

  const seasonDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seasonDropdownRef.current && !seasonDropdownRef.current.contains(event.target)) {
        setIsSeasonDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only offer seasons that actually have gameweek data behind them.
  useEffect(() => {
    const fetchSeasons = async () => {
      const [seasonsResult, gameweeksResult] = await Promise.all([
        supabase.from('seasons').select('id, name, is_current').order('start_date', { ascending: false }),
        supabase.from('gameweeks').select('season_id')
      ]);

      if (seasonsResult.error || !seasonsResult.data) return;

      const seasonIdsWithData = new Set((gameweeksResult.data || []).map(gw => gw.season_id));
      const available = seasonsResult.data.filter(s => seasonIdsWithData.has(s.id));

      setSeasons(available);
      const current = available.find(s => s.is_current) || available[0];
      if (current) setSelectedSeasonId(current.id);
    };

    fetchSeasons();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;

    const fetchTeamStats = async () => {
      setLoading(true);
      try {
        const cached = statsCache.get(selectedSeasonId);
        if (cached) {
          setTeamStats(cached.teamStats);
          setHomeStats(cached.homeStats);
          setAwayStats(cached.awayStats);
          return;
        }

        // Fetch all team stats from Supabase, plus the stale-Supabase-id -> this-
        // season's-dummy.js-id translation map (RPC results key by team_id on
        // Supabase's own numbering, not this season's ids)
        const [overallResult, homeResult, awayResult, idMap] = await Promise.all([
          supabase.rpc('get_team_xg_stats', { p_season_id: selectedSeasonId }),
          supabase.rpc('get_team_home_stats', { p_season_id: selectedSeasonId }),
          supabase.rpc('get_team_away_stats', { p_season_id: selectedSeasonId }),
          getSupaIdToLocalIdMap()
        ]);

        if (overallResult.error) {
          console.error('Error fetching team stats:', overallResult.error);
          return;
        }

        // Convert overall stats to object keyed by (translated) team_id
        const statsMap = {};
        overallResult.data.forEach(stat => {
          const localId = idMap[stat.team_id];
          const team = localId != null ? teams.find(t => t.id === localId) : null;
          if (team) {
            statsMap[localId] = {
              teamName: team.name,
              badge: team.badge,
              totalXG: parseFloat(stat.total_xg) || 0,
              totalXGC: parseFloat(stat.total_xgc) || 0,
              totalGoals: stat.total_goals || 0,
              totalConceded: stat.total_goals_conceded || 0,
            };
          }
        });

        // Convert home stats to object keyed by (translated) team_id
        const homeStatsMap = {};
        if (!homeResult.error && homeResult.data) {
          homeResult.data.forEach(stat => {
            const localId = idMap[stat.team_id];
            const team = localId != null ? teams.find(t => t.id === localId) : null;
            if (team) {
              homeStatsMap[localId] = {
                teamName: team.name,
                badge: team.badge,
                xg: parseFloat(stat.total_xg) || 0,
                xgc: parseFloat(stat.total_xgc) || 0,
                goals: stat.total_goals || 0,
                conceded: stat.total_goals_conceded || 0,
              };
            }
          });
        }

        // Convert away stats to object keyed by (translated) team_id
        const awayStatsMap = {};
        if (!awayResult.error && awayResult.data) {
          awayResult.data.forEach(stat => {
            const localId = idMap[stat.team_id];
            const team = localId != null ? teams.find(t => t.id === localId) : null;
            if (team) {
              awayStatsMap[localId] = {
                teamName: team.name,
                badge: team.badge,
                xg: parseFloat(stat.total_xg) || 0,
                xgc: parseFloat(stat.total_xgc) || 0,
                goals: stat.total_goals || 0,
                conceded: stat.total_goals_conceded || 0,
              };
            }
          });
        }

        statsCache.set(selectedSeasonId, { teamStats: statsMap, homeStats: homeStatsMap, awayStats: awayStatsMap });
        setTeamStats(statsMap);
        setHomeStats(homeStatsMap);
        setAwayStats(awayStatsMap);
      } catch (error) {
        console.error('Failed to fetch team stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStats();
  }, [teams, selectedSeasonId]);

  const handleSeasonSelect = (seasonId) => {
    setSelectedSeasonId(seasonId);
    setIsSeasonDropdownOpen(false);
  };

  const selectedSeasonName = seasons.find(s => s.id === selectedSeasonId)?.name || '';

  const allTeamsXG = Object.values(teamStats)
    .sort((a, b) => b.totalXG - a.totalXG);

  const allTeamsXGC = Object.values(teamStats)
    .sort((a, b) => a.totalXGC - b.totalXGC);

  // xG over/under performance (goals scored vs xG)
  const allTeamsXGPerformance = Object.values(teamStats)
    .map(team => ({
      ...team,
      xgDiff: team.totalGoals - team.totalXG
    }))
    .sort((a, b) => a.xgDiff - b.xgDiff);

  // xGC over/under performance (goals conceded vs xGC)
  const allTeamsXGCPerformance = Object.values(teamStats)
    .map(team => ({
      ...team,
      xgcDiff: team.totalConceded - team.totalXGC
    }))
    .sort((a, b) => b.xgcDiff - a.xgcDiff);

  // Overall strength (xG - xGC for all games)
  const allTeamsOverallStrength = Object.values(teamStats)
    .map(team => ({
      ...team,
      strength: team.totalXG - team.totalXGC,
      goalDiff: team.totalGoals - team.totalConceded
    }))
    .sort((a, b) => b.strength - a.strength);

  // Home strength (xG - xGC for home games)
  const allTeamsHomeStrength = Object.values(homeStats)
    .map(team => ({
      ...team,
      strength: team.xg - team.xgc,
      goalDiff: team.goals - team.conceded
    }))
    .sort((a, b) => b.strength - a.strength);

  // Away strength (xG - xGC for away games)
  const allTeamsAwayStrength = Object.values(awayStats)
    .map(team => ({
      ...team,
      strength: team.xg - team.xgc,
      goalDiff: team.goals - team.conceded
    }))
    .sort((a, b) => b.strength - a.strength);

  if (loading) {
    return (
      <div className="teams-page">
        <div className="loading-message">
          <p>⏳ Loading Team Stats... ⏳</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teams-page">
      <div className="top-10-sub-heading">
        <p>Below is a range of stats for all 20 of the Premier League teams.</p>
        <p>Use the data below to help identify which team has been over or under performing their underlying data!</p>
      </div>

      {seasons.length > 1 && (
        <div className="teams-dropdown-container">
          <div className="dropdown" ref={seasonDropdownRef} onClick={() => setIsSeasonDropdownOpen(prev => !prev)}>
            <span><strong>{selectedSeasonName}</strong></span>
            <span className="dropdown-arrow">▼</span>
            {isSeasonDropdownOpen && (
              <div className="dropdown-list">
                {seasons.map(season => (
                  <div key={season.id} className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleSeasonSelect(season.id); }}>
                    <input type="radio" checked={selectedSeasonId === season.id} readOnly />
                    {season.name}{season.is_current ? ' (Current)' : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">Team xG (Goals Scored)</p>
        {allTeamsXG.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsXG.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.totalXG.toFixed(2)}</p>
                <p className="player-stat">({team.totalGoals})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">Team xGC (Goals Conceded)</p>
        {allTeamsXGC.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsXGC.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.totalXGC.toFixed(2)}</p>
                <p className="player-stat">({team.totalConceded})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">Overall Strength xG-xGC (Goals-Conceded)</p>
        {allTeamsOverallStrength.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsOverallStrength.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.strength > 0 ? '+' : ''}{team.strength.toFixed(2)}</p>
                <p className="player-stat">({team.goalDiff > 0 ? '+' : ''}{team.goalDiff})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">Home Strength xG-xGC (Goals-Conceded)</p>
        {allTeamsHomeStrength.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsHomeStrength.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.strength > 0 ? '+' : ''}{team.strength.toFixed(2)}</p>
                <p className="player-stat">({team.goalDiff > 0 ? '+' : ''}{team.goalDiff})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">Away Strength  xG-xGC (Goals-Conceded)</p>
        {allTeamsAwayStrength.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsAwayStrength.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.strength > 0 ? '+' : ''}{team.strength.toFixed(2)}</p>
                <p className="player-stat">({team.goalDiff > 0 ? '+' : ''}{team.goalDiff})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">xG Over/Under Performance (Goals Scored)</p>
        {allTeamsXGPerformance.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsXGPerformance.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.xgDiff > 0 ? '+' : ''}{team.xgDiff.toFixed(2)}</p>
                <p className="player-stat">({team.totalGoals})</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="player-pics player-pics-lists">
        <p className="top-10-title">xGC Over/Under Performance (Goals Conceded)</p>
        {allTeamsXGCPerformance.length > 0 && (
          <div className="pics-wrapper category-scroll-wrapper">
            {allTeamsXGCPerformance.map((team, index) => (
              <div key={index} className="player-pic-container">
                <div className="player-rank">#{index + 1}</div>
                {team.badge ? (
                  <img className="team-pic-top-10" src={team.badge} alt={team.teamName} />
                ) : (
                  <p>Badge not available</p>
                )}
                <p className="player-stat-name">{team.teamName}</p>
                <p className="player-stat">{team.xgcDiff > 0 ? '+' : ''}{team.xgcDiff.toFixed(2)}</p>
                <p className="player-stat">({team.totalConceded})</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamsPage;
